import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import type { Unit } from '../../api/objects'
import { updateUnit } from '../../api/objects-write'
import type { UnitKind } from '../../api/objects-write'
import { t } from '../../i18n'
import { asksFurniture, asksPrivacy, numbersFor } from './unitFields'

/**
 * Changing what a space is: how big, how many rooms, how many people.
 *
 * All of it optional, and every field distinguishes empty from zero. A flat
 * nobody has measured has an unmeasured area, not an area of nothing; a room
 * nobody has counted the beds in has no answer, not none. That is invariant 2,
 * and a form is where it is most easily lost, because an empty number input
 * looks exactly like a nought to whoever wrote the code and nothing at all to
 * whoever filled it in.
 *
 * A field left as it was is not sent. A field cleared is sent as null, which is
 * what makes clearing possible at all: the two are different requests and this
 * is the only place the difference is visible.
 */
export function EditUnitForm({ unit, propertyID }: { readonly unit: Unit; readonly propertyID: string }) {
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const [label, setLabel] = useState(unit.label)
  const [values, setValues] = useState<Record<string, string>>({
    area_sqm: numberField(unit.areaSqm),
    capacity: numberField(unit.capacity),
    bedrooms: numberField(unit.bedrooms),
    beds: numberField(unit.beds),
    bathrooms: numberField(unit.bathrooms),
  })
  const [furnished, setFurnished] = useState(triState(unit.furnished))
  const [roomPrivate, setRoomPrivate] = useState(triState(unit.roomPrivate))
  const [notes, setNotes] = useState(unit.notes)

  const client = useQueryClient()

  // Cancel has to put the fields back, not merely hide them. It used to close
  // the form and keep whatever had been typed, so reopening showed abandoned
  // edits as though they had been saved -- and the next Save wrote them.
  function reset() {
    setLabel(unit.label)
    setValues({
      area_sqm: numberField(unit.areaSqm),
      capacity: numberField(unit.capacity),
      bedrooms: numberField(unit.bedrooms),
      beds: numberField(unit.beds),
      bathrooms: numberField(unit.bathrooms),
    })
    setFurnished(triState(unit.furnished))
    setRoomPrivate(triState(unit.roomPrivate))
    setNotes(unit.notes)
    setFailure(null)
  }

  // Which numbers are worth asking about this kind of space, declared once in
  // unitFields.ts. A room has no bedrooms and a place has nothing to count.
  const numbers = numbersFor(unit.kind as UnitKind).map((field) => ({
    ...field,
    // Empty is the value for a field this space has never been asked about.
    value: values[field.id] ?? '',
    set: (next: string) => setValues((current) => ({ ...current, [field.id]: next })),
  }))


  const save = useMutation({
    mutationFn: () =>
      updateUnit(unit.id, {
        label,
        notes,
        // Cleared fields go as null on purpose. Absent would leave them as they
        // were, and there would be no way to unsay a number typed by mistake.
        //
        // Only the fields this kind of space is asked about are sent. A room's
        // bedroom count is not cleared here because it was never a question, and
        // sending null for it would erase whatever an import had recorded.
        ...Object.fromEntries(numbers.map((field) => [field.id, nullable(field.value)])),
        ...(asksFurniture(unit.kind as UnitKind) ? { furnished: fromTriState(furnished) } : {}),
        ...(asksPrivacy(unit.kind as UnitKind) ? { room_private: fromTriState(roomPrivate) } : {}),
      }),
    onSuccess: () => {
      setFailure(null)
      setOpen(false)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t('build.edit')}
      </Button>
    )
  }

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor={`edit-label-${unit.id}`}>
          {t('build.label')}
        </label>
        <input
          id={`edit-label-${unit.id}`}
          className="field__input"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>

      <div className="build-form__grid">
        {numbers.map((field) => (
          <div className="field" key={field.id}>
            <label className="field__label" htmlFor={`edit-${field.id}-${unit.id}`}>
              {t(field.label)}
            </label>
            <input
              id={`edit-${field.id}-${unit.id}`}
              className="field__input"
              type="number"
              min={field.min}
              {...(field.step === undefined ? {} : { step: field.step })}
              value={field.value}
              onChange={(event) => field.set(event.target.value)}
              placeholder={t('build.notAsked')}
            />
            {field.hint === undefined ? null : <p className="field__hint">{t(field.hint)}</p>}
          </div>
        ))}
      </div>

      <div className="build-form__grid">
        {asksFurniture(unit.kind as UnitKind) ? (
          <TriStateField
            id={`edit-furnished-${unit.id}`}
            label={t('build.furnished')}
            value={furnished}
            onChange={setFurnished}
          />
        ) : null}
        {/* Only a room can be somebody's own. A flat is by definition, and a
            place is by definition not -- that is what makes it a place. */}
        {asksPrivacy(unit.kind as UnitKind) ? (
          <TriStateField
            id={`edit-private-${unit.id}`}
            label={t('build.roomPrivate')}
            value={roomPrivate}
            onChange={setRoomPrivate}
          />
        ) : null}
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`edit-notes-${unit.id}`}>
          {t('build.notes')}
        </label>
        <textarea
          id={`edit-notes-${unit.id}`}
          className="field__input"
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={save.isPending}>
          {save.isPending ? t('build.saving') : t('build.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          disabled={save.isPending}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

/**
 * Three answers and not two.
 *
 * "Not asked" is a real answer and the commonest one: a client who insisted on a
 * private room must never be shown an unasked room as if it were a yes. A
 * checkbox has no way to say it, so this is a select.
 */
function TriStateField({
  id,
  label,
  value,
  onChange,
}: {
  readonly id: string
  readonly label: string
  readonly value: string
  readonly onChange: (next: string) => void
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field__input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{t('build.notAsked')}</option>
        <option value="yes">{t('common.yes')}</option>
        <option value="no">{t('common.no')}</option>
      </select>
    </div>
  )
}

function numberField(value: number | undefined): string {
  return value === undefined ? '' : String(value)
}

function nullable(value: string): number | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : Number(trimmed)
}

function triState(value: boolean | undefined): string {
  if (value === undefined) return ''
  return value ? 'yes' : 'no'
}

function fromTriState(value: string): boolean | null {
  if (value === '') return null
  return value === 'yes'
}
